import axios from "axios";
import { useAuthStore } from "../store/authStore.js";
import { API_BASE_URL } from "../consts.js";

/**
 * Axios instance pre-configured for the Monitero API.
 *
 * Features:
 *  - Automatically attaches the in-memory access token as Authorization header
 *  - Intercepts 401 responses and attempts a silent token refresh
 *  - Retries the original request once after a successful refresh
 *  - Calls logout() if the refresh also fails (session expired)
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // send httpOnly refresh token cookie on all requests
  headers: {
    "Content-Type": "application/json",
  },
});

// ---------------------------------------------------------------------------
// Request interceptor — attach access token and active workspace
// ---------------------------------------------------------------------------
apiClient.interceptors.request.use(
  (config) => {
    const { accessToken, workspace } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    if (workspace?.id) {
      config.headers["X-Workspace-Id"] = workspace.id;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------------
// Response interceptor — handle 401 with silent refresh
// ---------------------------------------------------------------------------

let _isRefreshing = false;
let _refreshSubscribers = [];

/** Queue a callback to be called after a successful token refresh. */
function _subscribeRefresh(callback) {
  _refreshSubscribers.push(callback);
}

/** Notify all queued requests of the new access token. */
function _onRefreshSuccess(newToken) {
  _refreshSubscribers.forEach((cb) => cb(newToken));
  _refreshSubscribers = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 errors, and only retry once
    if (error.response?.status !== 401 || originalRequest._retried) {
      return Promise.reject(error);
    }

    originalRequest._retried = true;

    if (_isRefreshing) {
      // Another request is already refreshing — queue this one
      return new Promise((resolve) => {
        _subscribeRefresh((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolve(apiClient(originalRequest));
        });
      });
    }

    _isRefreshing = true;

    try {
      // Attempt silent refresh using the httpOnly cookie
      const response = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true }
      );
      const sessionData = response.data?.data;
      const newToken = sessionData?.access_token;

      if (!newToken) throw new Error("No access token in refresh response");

      useAuthStore.getState().setSession(sessionData);
      _onRefreshSuccess(newToken);

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return apiClient(originalRequest);
    } catch (_refreshError) {
      // Refresh failed — force logout
      useAuthStore.getState().logout();
      return Promise.reject(error);
    } finally {
      _isRefreshing = false;
    }
  }
);

export default apiClient;
